import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import {
  Warning,
  MapPin,
  Clock,
  ArrowRight,
  Fire,
  ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * Safety Board — war-room view of every open life-safety WO across the tenant.
 * Auto-refreshes every 30 seconds. Aging tiles color-shift as time passes.
 */
export default function SafetyBoard() {
  const [data, setData] = useState({ jobs: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/safety-board", {
        params: { refresh: Date.now() },
      });
      setData(r.data);
    } catch (e) { /* silent */ }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <AdminShell>
      <div className="p-8">
        <div className="flex items-center gap-3">
          <Fire weight="fill" size={32} className="text-red-600"/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-red-700">War Room</div>
            <h1 className="font-display font-black text-3xl">Safety Board</h1>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              data-testid="safety-board-refresh-button"
              className="h-10 w-10 border-2 border-slate-900 bg-white text-slate-800 "
              aria-label="Refresh safety board"
            >
              <ArrowsClockwise
                weight="bold"
                size={18}
                className={refreshing ? "animate-spin" : ""}
              />
            </button>
            <div className="text-right">
              <div className="font-display font-black text-4xl leading-none text-red-600" data-testid="safety-count">{data.count}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Open · Life-Safety</div>
              {data.generated_at && (
                <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400"
                  data-testid="safety-board-updated-at">
                  Fresh {new Date(data.generated_at).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="mt-2 text-slate-500 text-sm max-w-2xl">
          Every open leak, gas, no-heat, sparking, lockout, mold, alarm & sewer job — sorted by age. The longer they sit, the redder they get. Assign fast.
        </p>

        {loading && data.jobs.length === 0 && (
          <div className="mt-8 font-bold text-slate-500">Loading…</div>
        )}

        {!loading && data.jobs.length === 0 && (
          <div className="mt-8 rugged-card p-8 text-center">
            <div className="font-display font-black text-4xl">🎉</div>
            <div className="mt-3 font-display font-black text-2xl">All clear</div>
            <p className="mt-2 text-slate-500 text-sm">No open life-safety jobs right now. Nice work.</p>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.jobs.map(j => <SafetyTile key={j.id} job={j}/>)}
        </div>
      </div>
    </AdminShell>
  );
}

function SafetyTile({ job }) {
  const h = job.age_hours;
  // Aging color scale
  let band = "bg-emerald-500", label = "FRESH";
  if (h == null) { band = "bg-slate-500"; label = "?"; }
  else if (h >= 24) { band = "bg-red-700 animate-pulse"; label = `${Math.floor(h / 24)}d ${h % 24}h`; }
  else if (h >= 8)  { band = "bg-red-600"; label = `${h}h`; }
  else if (h >= 4)  { band = "bg-orange-500"; label = `${h}h`; }
  else if (h >= 1)  { band = "bg-yellow-500"; label = `${h}h`; }
  else { band = "bg-emerald-500"; label = "< 1h"; }
  const unassigned = !job.assigned_to_name;

  return (
    <Link to={`/admin/jobs/${job.id}`} data-testid={`safety-tile-${job.id}`}
      className="rugged-card border-l-8 border-red-600 hover:border-l-[12px] transition-all overflow-hidden group">
      <div className={`${band} text-white px-3 py-1.5 flex items-center justify-between`}>
        <span className="inline-flex items-center gap-1.5 font-black uppercase text-[11px] tracking-widest">
          <Clock weight="fill" size={12}/> {label} old
        </span>
        <span className="font-black uppercase text-[10px] tracking-widest">{job.priority}</span>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2">
          <Warning weight="fill" size={22} className="text-red-600 flex-shrink-0 mt-1"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-lg leading-tight line-clamp-2">{job.title}</div>
            {job.wo && (
              <div className="mt-1 inline-flex items-center h-4 px-1 bg-[#0f172a] text-white text-[9px] font-black uppercase tracking-widest font-mono">
                WO# {job.wo}
              </div>
            )}
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-600 font-medium">
              <MapPin weight="bold" size={12}/>
              <span className="truncate">{job.property_address}{job.unit ? ` · #${job.unit}` : ""}</span>
            </div>
            {job.description && (
              <div className="mt-2 text-xs text-slate-500 line-clamp-2">{job.description}</div>
            )}
            <div className="mt-3 flex items-center gap-2">
              {unassigned ? (
                <span className="inline-flex items-center h-6 px-2 bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest">Unassigned</span>
              ) : (
                <span className="inline-flex items-center h-6 px-2 bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest">{job.assigned_to_name}</span>
              )}
              <span className="inline-flex items-center h-6 px-2 bg-slate-200 text-slate-800 text-[10px] font-black uppercase tracking-widest">{job.status}</span>
              <ArrowRight weight="bold" size={14} className="ml-auto text-slate-300 group-hover:text-red-600"/>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
